import type { Product } from '@commerce/types/product'
import WishlistButton from '@components/arkade/list/WishlistButton'
import { SeoHead } from '@components/common'
import {
  ProductColourWall,
  ProductImage,
  ProductSidebar,
  ProductSlider,
} from '@components/product'
import { Container } from '@components/ui'
import { findBundleInfoFromMetafield } from '@lib/helpers/general'

import { PrismicDocument } from '@prismicio/types'
import { useRouter } from 'next/router'
import { FC, useState } from 'react'

interface ProductViewProps {
  product: Product
  productSiblings: Product[]
  prismicContent: PrismicDocument | any
}

const ProductView: FC<ProductViewProps> = ({
  product,
  productSiblings,
  prismicContent,
}) => {

  const router = useRouter()
  const [selectedSibling, setSelectedSibling] = useState<Product>()
  const bundleInfo = findBundleInfoFromMetafield(product.metafields)
  const isBundleProduct = !!bundleInfo

  return (
    <>
      <div className="w-full bg-ui-light-grey">
        <Container className="w-full max-w-none">
          <div className="grid grid-cols-6 gap-2 pb-15 lg:gap-4 lg:pb-20">
            {product.productType === 'Colour' && (
              <>
                <div className="col-span-6 md:col-span-3 lg:col-span-2">
                  <ProductColourWall colour={product.colourCode} />
                </div>
                <div className="relative col-span-6 md:col-span-3 lg:col-span-2">
                  <ProductSlider images={product.images} />
                  <div className="absolute top-5 right-5 z-10">
                    <WishlistButton product={product} />
                  </div>
                </div>
              </>
            )}
            {isBundleProduct && (
              <>
                <div className="col-span-6 hidden md:col-span-3 md:block lg:col-span-2">
                  <ProductImage
                    img={product.images?.[0] ? product.images[0] : undefined}
                  />
                  <div className="body-2 mt-2 min-h-[20px] text-ui-dark-grey line-clamp-1">
                    {product?.images?.[0]?.altText}
                  </div>
                </div>
                <div className="relative col-span-6 hidden  md:col-span-3 md:block lg:col-span-2">
                  <ProductSlider images={product.images?.slice(1)} />
                  <div className="absolute top-5 right-5 z-10">
                    <WishlistButton product={product} />
                  </div>
                </div>
                <div className="relative col-span-6 block md:col-span-3 md:hidden lg:col-span-2">
                  <ProductSlider images={product.images} />
                  <div className="absolute top-5 right-5 z-10">
                    <WishlistButton product={product} />
                  </div>
                </div>
              </>
            )}
            {product.productType !== 'Colour' && !isBundleProduct && (
              <>
                <div className="col-span-6 hidden md:col-span-3 md:block lg:col-span-2">
                  <ProductImage
                    img={product.images?.[0] ? product.images[0] : undefined}
                  />
                  <div className="body-2 mt-2 min-h-[20px] text-ui-dark-grey line-clamp-1">
                    {product?.images?.[0]?.altText}
                  </div>
                </div>
                <div className="col-span-6 md:col-span-3 lg:col-span-2">
                  <ProductImage
                    img={product.images?.[1] ? product.images[1] : undefined}
                  />
                  <div className="body-2 mt-2 min-h-[20px] text-ui-dark-grey line-clamp-1">
                    {product?.images?.[1]?.altText}
                  </div>
                </div>
              </>
            )}

            <div key={router.locale} className="col-span-6 lg:col-span-2">
              <ProductSidebar
                product={product}
                productSiblings={productSiblings}
                prismicContent={prismicContent?.data}
                selectedSibling={selectedSibling}
                setSelectedSibling={setSelectedSibling}
              />
            </div>
          </div>
        </Container>
      </div>

      <SeoHead
        title={product.name}
        description={product.description}
        image={{ url: product.images[0]?.url! }}
      />
    </>
  )
}

export default ProductView
